import get from 'lodash/get';
import find from 'lodash/find';
import Inputmask from 'inputmask';
import pmt from '../../utils/pmt';
import { formatCurrency } from '../../utils/numberFormat';

export default ({ locale, currency, clienteles, contractTypes }) => ({
  locale: locale,
  currency: currency,
  clienteles: clienteles,
  contractTypes: contractTypes,
  processed: false,
  formData: {
    netInvestment: null,
    clientele: null,
    contractType: null,
    financialSupport: 0,
  },

  /**
   * Determine if the form is valid.
   *
   * @return {boolean}
   */
  get isValid() {
    return (
      this.formData.netInvestment &&
      this.formData.clientele &&
      this.formData.contractType &&
      this.formData.financialSupport !== null
    );
  },

  /**
   * Calculate the borrowed amount.
   *
   * @return {Number}
   */
  get amountBorrowed() {
    if (!this.isValid) {
      return 0;
    }

    let financialSupport = Math.floor(this.formData.netInvestment * (this.formData.financialSupport / 100));

    return this.formData.netInvestment - financialSupport;
  },

  /**
   * Calculate total costs for taxes and fees.
   *
   * @return {Number}
   */
  get taxAndFeesCost() {
    if (!this.isValid) {
      return 0;
    }

    if (this.formData.netInvestment >= 500_000) {
      return (this.formData.netInvestment * 0.0991) - 2_874;
    }

    if (this.formData.netInvestment >= 250_000) {
      return (this.formData.netInvestment * 0.0991) + 1_631;
    }

    if (this.formData.netInvestment >= 200_000) {
      return (this.formData.netInvestment * 0.171) - 3_491;
    }

    if (this.formData.netInvestment >= 50_000) {
      return (this.formData.netInvestment * 0.091) + 736;
    }

    if (this.formData.netInvestment >= 23_833.33) {
      return (this.formData.netInvestment * 0.115) - 611;
    }

    return (this.formData.netInvestment * 0.091) + 23;
  },

  /**
   * Calculate the expected yearly income.
   *
   * @return {Number}
   */
  get expectedYearlyIncome() {
    if (!this.isValid) {
      return 0;
    }

    let clienteleObject = find(this.clienteles, { id: this.formData.clientele });

    if (!clienteleObject) {
      return 0;
    }

    let minCoefficient = get(clienteleObject, ['coefficients', this.formData.contractType, 'min']);
    let maxCoefficient = get(clienteleObject, ['coefficients', this.formData.contractType, 'max']);
    let averageCoefficient = ((minCoefficient + maxCoefficient) / 2);

    return Math.floor((this.formData.netInvestment - this.taxAndFeesCost) / averageCoefficient);
  },

  /**
   * Calculate the expected monthly income.
   *
   * @return {Number}
   */
  get expectedMonthlyIncome() {
    if (!this.isValid) {
      return 0;
    }

    return Math.floor(
      pmt(0.01 / 12, 84, this.amountBorrowed, 0, 0) + (this.expectedYearlyIncome / 12)
    );
  },

  /**
   * Calculate results.
   */
  calculate() {
    if (this.isValid) {
      this.processed = true;
    }
  },

  /**
   * Reset the form ans calculate again.
   */
  resetForm() {
    this.processed = false;

    this.formData.netInvestment = null;
    this.formData.clientele = null;
    this.formData.contractType = null;
    this.formData.financialSupport = 0;
  },

  /**
   * Format currency value.
   *
   * @param {Number} amount
   * @return {string}
   */
  formattedCurrency(amount) {
    return formatCurrency(amount, this.currency.code, this.locale);
  },

  init() {
    Inputmask('currency', {
      numericInput: true,
      rightAlign: false,
      autoUnmask: true,
      groupSeparator: locale === 'fr' ? '.' : ',',
      digits: 0,
      onBeforeMask: function (value, opts) {
        if (null === value) { // fix charAt on null errors
          value = '0';
        }

        return value;
      },
    }).mask(this.$refs.net_investment);
  },
});
